import { ORB } from '@blorbs/common';
import type { PlayerState } from '@blorbs/common';

/** How much a full-speed orb stretches along its direction of travel (fraction). */
const STRETCH_AT_MAX_SPEED = 0.09;
/** Hover while moving: height (world units) at full speed, and bobs per second at full speed. */
const HOVER_HEIGHT = 6.5;
const HOVER_RATE = 2.1;
/** Idle breathing: amplitude (fraction, vertical) and rate (rad/s ≈ a 2 s cycle). Fades out with speed. */
const BREATH = 0.055;
const BREATH_RATE = 3.0;
/** Jelly spring: natural frequency (rad/s) and damping ratio. ~2 Hz, three visible bounces. */
const OMEGA = 13;
const ZETA = 0.3;
const MAX_WOBBLE = 0.22;
/** Shot recoil: its own stiffer, quicker spring on the aim axis (~0.3 s pop). */
const RECOIL_OMEGA = 12;
const RECOIL_ZETA = 0.32;

interface State {
  /** smoothed velocity, for the stretch axis */
  vx: number;
  vy: number;
  /** last axis angle, kept while standing still so a wobble has a direction */
  angle: number;
  /** jelly spring: deformation and its velocity */
  w: number;
  wv: number;
  /** server time of the last damage we reacted to */
  lastHitAt: number;
  seen: number;
  /** hover phase (radians), advances with speed */
  bob: number;
  /** current hover height, eased so stopping settles instead of snapping */
  lift: number;
  /** shot recoil spring on the aim axis */
  rAngle: number;
  rw: number;
  rwv: number;
}

export interface Deform {
  /** axis of the stretch, radians */
  angle: number;
  /** scale along the axis and across it (area-preserving: sy = 1/sx) */
  sx: number;
  sy: number;
  /** how far the body floats above its shadow (world units) */
  lift: number;
  /** inhale amount 0..1 while breathing (the shadow shrinks with it) */
  puff: number;
  /** true when the deformation axis is vertical (standing): the stretch is anchored at the base */
  upright: boolean;
}

/**
 * Squash & stretch for the orbs. Movement stretches an orb along its
 * velocity (and squashes it across), impulses (hits, shoves, landing a
 * teleport, the recoil of a shot) kick a damped spring that makes it wobble
 * like jelly, and an idle breath keeps a standing orb alive.
 */
export class Squish {
  private states = new Map<string, State>();

  /** Kick the jelly. `amount` ≈ 0.15 (a shove) … 0.35 (a hard hit). */
  impulse(id: string, amount: number): void {
    const s = this.states.get(id);
    if (!s) return;
    s.wv += amount * OMEGA * 2.2;
  }

  /** A shot: compress back along the aim, then spring forward and settle. */
  recoil(id: string, aimAngle: number, amount = 0.3): void {
    const s = this.states.get(id);
    if (!s) return;
    s.rAngle = aimAngle;
    s.rwv -= amount * RECOIL_OMEGA * 2.4;
  }

  update(dt: number, players: PlayerState[], now: number): void {
    for (const p of players) {
      let s = this.states.get(p.id);
      if (!s) {
        s = { vx: 0, vy: 0, angle: 0, w: 0, wv: 0, lastHitAt: p.lastHitAt, seen: now, bob: Math.random() * Math.PI * 2, lift: 0, rAngle: 0, rw: 0, rwv: 0 };
        this.states.set(p.id, s);
      }
      s.seen = now;
      // Any new damage (fireball, shockwave shove, lava) → a jolt, rate-limited so lava doesn't buzz.
      if (p.lastHitAt > s.lastHitAt + 140) {
        s.lastHitAt = p.lastHitAt;
        this.impulse(p.id, 0.17);
      }
      // Smooth the velocity so the axis doesn't flicker with input changes.
      const k = 1 - Math.exp(-dt * 18);
      const tx = p.alive ? p.vx + p.kx : 0;
      const ty = p.alive ? p.vy + p.ky : 0;
      s.vx += (tx - s.vx) * k;
      s.vy += (ty - s.vy) * k;
      const speed = Math.hypot(s.vx, s.vy);
      if (speed > 12) s.angle = Math.atan2(s.vy, s.vx);
      // Hover: the bob runs faster and higher with speed, and eases out on stopping.
      const pace = Math.min(1, speed / ORB.maxSpeed);
      s.bob += dt * Math.PI * 2 * HOVER_RATE * (0.35 + 0.65 * pace);
      const targetLift = p.alive ? HOVER_HEIGHT * pace * (0.5 + 0.5 * Math.sin(s.bob)) : 0;
      s.lift += (targetLift - s.lift) * (1 - Math.exp(-dt * 14));
      // Damped springs toward 0: the general wobble and the shot recoil.
      const acc = -OMEGA * OMEGA * s.w - 2 * ZETA * OMEGA * s.wv;
      s.wv += acc * dt;
      s.w += s.wv * dt;
      s.w = Math.max(-MAX_WOBBLE, Math.min(MAX_WOBBLE, s.w));
      const racc = -RECOIL_OMEGA * RECOIL_OMEGA * s.rw - 2 * RECOIL_ZETA * RECOIL_OMEGA * s.rwv;
      s.rwv += racc * dt;
      s.rw += s.rwv * dt;
      s.rw = Math.max(-0.38, Math.min(0.28, s.rw));
    }
    // Forget orbs that left.
    for (const [id, s] of this.states) if (now - s.seen > 2) this.states.delete(id);
  }

  get(p: PlayerState, now: number): Deform {
    const s = this.states.get(p.id);
    if (!s || !p.alive) return { angle: 0, sx: 1, sy: 1, lift: 0, puff: 0, upright: false };
    const speed = Math.hypot(s.vx, s.vy);
    const pace = Math.min(1, speed / ORB.maxSpeed);
    const stretch = STRETCH_AT_MAX_SPEED * pace;
    // Breathing is a vertical pulse (inhale = taller) that gives way to the travel stretch as the orb moves.
    const still = 1 - Math.min(1, pace * 3);
    const wave = Math.sin(now * BREATH_RATE + hashId(p.id));
    const breath = BREATH * still * wave;
    // While a shot's recoil is live it owns the axis (the aim); the rest is folded in lightly.
    if (Math.abs(s.rw) > 0.004 || Math.abs(s.rwv) > 0.05) {
      const a = s.rw + 0.4 * (stretch + s.w + breath);
      const sx = Math.max(0.55, 1 + a);
      return { angle: s.rAngle, sx, sy: 1 / sx, lift: s.lift, puff: still * Math.max(0, wave) * 0.5, upright: false };
    }
    // Standing: the axis is vertical so the breath and any wobble go up/down; moving: along the travel direction.
    const upright = still > 0.5;
    const angle = upright ? -Math.PI / 2 : s.angle;
    // Wobble stretches along the axis on the positive half and across it on the negative half.
    const a = stretch + s.w + breath;
    const sx = Math.max(0.55, 1 + a);
    return { angle, sx, sy: 1 / sx, lift: s.lift, puff: still * Math.max(0, wave), upright };
  }
}

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 1000;
  return h;
}
